import type { ModelOption } from '../lib/models';

interface Props {
  label: string;
  prompt: string;
  options: ModelOption[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}

export function ModelSelector({ label, prompt, options, value, onChange, disabled }: Props) {
  const selected = options.find((o) => o.id === value) ?? options[0];

  return (
    <div className="space-y-2">
      <p className="label-mono">
        <span className="text-[var(--color-amber-mid)]">$</span> {label}
        <span className="text-[var(--color-asphalt-300)]"> // {prompt}</span>
      </p>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
        {options.map((opt) => {
          const active = opt.id === value;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => onChange(opt.id)}
              className={[
                'group flex flex-col items-start gap-0.5 px-3 py-2 transition-all border',
                'font-mono text-left',
                active
                  ? 'border-[var(--color-amber-mid)] bg-[rgba(0,255,170,0.06)] text-[var(--color-amber-mid)]'
                  : 'border-[var(--color-asphalt-300)] text-[var(--color-asphalt-500)] hover:text-[var(--color-asphalt)] hover:border-[var(--color-asphalt-400)]',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              ].join(' ')}
              style={active ? { boxShadow: 'var(--glow-cyan)' } : undefined}
            >
              <span className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.08em]">
                <span className={active ? 'text-[var(--color-amber-mid)]' : 'text-[var(--color-asphalt-300)]'}>
                  {active ? '[x]' : '[ ]'}
                </span>
                {opt.label}
              </span>
              <span className="text-[10px] text-[var(--color-asphalt-400)] tracking-wide pl-[26px]">
                {opt.blurb}
              </span>
            </button>
          );
        })}
      </div>
      <p className="font-mono text-[10px] text-[var(--color-asphalt-400)] pt-1">
        <span className="text-[var(--color-asphalt-300)]">→ </span>
        <span className="text-[var(--color-asphalt-500)]">selected:</span>{' '}
        <span className="text-[var(--color-asphalt)]">{selected.id}</span>
      </p>
    </div>
  );
}
